import { useEffect, useRef, useState } from "react";

export type DashboardVersionStatus =
  | "up_to_date"
  | "update_available"
  | "not_configured"
  | "unavailable";

export type DashboardChannel = "stable" | "beta" | "dev";

export type DashboardVersionMeta = {
  currentCommit: string;
  currentCommitShort: string;
  currentVersion: string;
  latestCommit: string | null;
  latestCommitShort: string | null;
  latestVersion: string | null;
  updateAvailable: boolean;
  status: DashboardVersionStatus;
  lastCheckedAt: string | null;
  channel?: DashboardChannel;
  error?: string;
};

export type SystemMetaResponse = {
  health: "healthy" | "degraded" | "down";
  installedVersion: string;
  latestVersion: string | null;
  updateAvailable: boolean;
  updatedAt: string;
  message?: string;
  dashboardVersion?: DashboardVersionMeta;
  isDemoMode?: boolean;
};

const DEFAULT_REFRESH_MS = 60_000;

type HookState = {
  data?: SystemMetaResponse;
  loading: boolean;
  error?: string;
};

export const useSystemMeta = (options?: { refreshMs?: number }) => {
  const [state, setState] = useState<HookState>({ loading: true });
  const intervalRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchMeta = async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch("/api/system/meta", {
          cache: "no-store",
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          throw new Error(`Request failed (${res.status})`);
        }
        const payload = (await res.json()) as SystemMetaResponse;
        if (!mounted) return;
        setState({ data: payload, loading: false, error: undefined });
      } catch (error) {
        if (!mounted || abortRef.current?.signal.aborted) return;
        const message = error instanceof Error ? error.message : "Status unavailable";
        setState((prev) => ({ ...prev, loading: false, error: message }));
      }
    };

    fetchMeta();
    const refreshMs = options?.refreshMs ?? DEFAULT_REFRESH_MS;
    intervalRef.current = window.setInterval(fetchMeta, Math.max(5_000, refreshMs));

    return () => {
      mounted = false;
      abortRef.current?.abort();
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [options?.refreshMs]);

  return state;
};
